import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, ViewChild, ElementRef, OnDestroy, HostListener } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Activity, ActivityType, ActivityTypeToFilterNameMap, MeetingActivityState } from '@omni/classes/activity/activity.class';
import { ActivityService } from '@omni/services/activity/activity.service';
import { DeviceService } from '@omni/services/device/device.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { UIService } from '@omni/services/ui/ui.service';
import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';
import { EventsService } from '@omni/services/events/events.service';
import { VirtualListComponent } from '../angular-virtual-list';
import { AuthenticationService } from '@omni/services/authentication.service';
import { AppointmentActivity, CovisitorAccess } from '@omni/classes/activity/appointment.activity.class';
import { startRecursiveActivityCloseByActivityFlagging, startRecursiveActivityScheduleConflictFlagging } from '@omni/utility/activity.utility';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { DatePipe } from '@angular/common';
import { TimeOffActivity } from '@omni/classes/activity/timeoff.class';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import en from 'date-fns/locale/en';
import { MainCardViewDataModel } from '@omni/models/MainCardViewDataModel';
import { WebsocketDataService } from '@omni/data-services/websocket/websocket.data.service';
import { InitiateMeetingPayload, MeetingDataService } from '@omni/data-services/meeting/meeting.data.service';
import { CurViewPageType, DateTimeFieldType } from '../shared/ind-datetime-form/ind-datetime-form';
import { GoAppCalendarComponent } from '../go-app-calendar/go-app-calendar';
import { IndDropdownListComponent } from '../shared/ind-dropdown-list/ind-dropdown-list';
import { IndDropdownListDetailModel } from '@omni/models/indDropdownListModel';
import { OrderActivity } from '@omni/classes/activity/order.activity.class';
import { ProcedureTrackerActivity } from '@omni/classes/activity/procedure-tracker.activity.class';
import { StoreCheckActivity } from '@omni/classes/activity/store-check.activity.class';
import { CaseActivity } from '@omni/classes/case-intake/case-activity.class';
import { SetBookingActivity, SetBookingStatus } from '@omni/classes/activity/set-booking.activity.class';
import { EmailActivity } from '@omni/classes/activity/email.activity.class';
import { SurgeryOrderActivity } from '@omni/classes/activity/surgery-order.activity.class';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { Presentation } from '@omni/classes/presentation/presentation.class';
import { Resource } from '@omni/classes/resource/resource.class';
import { ActivityDataService } from '@omni/data-services/activity/activity.service';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { PresentationService } from '@omni/services/presentation/presentation.service';
import { ResourceService } from '@omni/services/resource/resource.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { NotificationService } from '@omni/services/notification/notification.service';
import { OpportunityManagementService } from '@omni/services/opportunity-management/opportunity-management.service';
import { Utility } from '@omni/utility/util';
import { CoachingReportService } from '@omni/services/coaching/coaching.report.service';
import { ActivityCalendarComponent } from '../activity/activity-calendar/activity-calendar';
import { IndFilterMenuModalComponent, IndFilterMenuModalDataModel } from '../shared/ind-filter-menu-modal/ind-filter-menu-modal';
import { NothingSelectedView } from '../shared/nothing-selected-view/nothing-selected-view';
import { GlobalUtilityService } from '@omni/services/global-utility.service';
import { ContactsModel } from '@omni/classes/contact/contact.class';
import { IndFilterMenuComponent } from '../shared/ind-filter-menu/ind-filter-menu';
import { ActivitiesPaneComponent } from '../activity/activities-pane/activities-pane';
import { AgendaFooterService } from '@omni/services/footer/agenda-footer.service';
import { SearchConfigService } from '@omni/services/search/search-config.service';
import { LocalizationService } from '@omni/services/localization/localization.service';

@Component({
  selector: 'go-app-activities',
  templateUrl: './go-app-activities.html',
  styleUrl: './go-app-activities.scss',
  // OnPush deferred — activityService.activitiesSearchText (two-way ngModel, set externally) and
  // activityService.multiOptionActivityFilter (set externally) have no observables
})
export class GoAppActivitiesComponent implements OnDestroy {
  @ViewChild(ActivitiesPaneComponent, { static: false }) private activitiesPane: ActivitiesPaneComponent;


  public calendarIsOpen: boolean = false;
  private _calendarIsMaximized: boolean = false;
  public get calendarIsMaximized() {
    return this._calendarIsMaximized;
  }
  public set calendarIsMaximized(value: boolean) {
    this._calendarIsMaximized = value;
    let element = this.el.nativeElement;
    let container = element.closest('go-app-activities-page');
    container.classList.toggle('calendar-maximized', this.calendarIsMaximized);
  }

  @Output() activityOpenedFromActivities = new EventEmitter<Activity>();

  @Output() newActivityOpened = new EventEmitter<boolean>();

  @Output() openFilter = new EventEmitter<any>();

  @ViewChild(ActivityCalendarComponent) activityCalendar: ActivityCalendarComponent;
  @ViewChild('container', { static: true }) containerRef: ElementRef;
  showSearchBar: boolean = false;
  filterFormView: IndFilterMenuModalDataModel[] = [];
  // selectedFilters: any = [];
  private resizeObserver: ResizeObserver;

  /** Local reactive copy — true when any activity is selected */
  hasSelectedActivity: boolean = false;
  /** Local reactive copy of activityService.newHomeView for template reads */
  newHomeView: string = 'listView';
  /**
   * True once the user manually switches view this session. Gates the
   * configured-default (re)application so background delta syncs never pull the
   * user off a view they chose.
   */
  private userSelectedHomeView = false;

  private filtersApplied: boolean = false;
  private readonly syncCompleted$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  activityPaneDate: Date | null = null;

  private get date_fns_locale() {
    return this.localizationService.getDateFnsLocaleObject() || { locale: en };
  }

  constructor(
    private translate: TranslateService,
    public readonly device: DeviceService,
    public activityService: ActivityService,
    private uiService: UIService,
    private events: EventsService,
    private authService: AuthenticationService,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private readonly agendFooterService: AgendaFooterService,
    private readonly el: ElementRef<HTMLElement>,
    private readonly searchConfigService: SearchConfigService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly localizationService: LocalizationService,
    private readonly _cd: ChangeDetectorRef,
  ) {
    this.applyConfiguredDefaultHomeView();
  }

  onTooltipVisibilityChange() {
    this._cd.markForCheck();
  }

  /**
   * Resolves the default activities home landing view from the IO configuration
   * `BU_ACTIVITY_GA_HOME_DEFAULT_VIEW` (value: 'agenda' | 'calendar'). Falls back
   * to the Agenda list ('listView') when the config is unset or unrecognised.
   */
  private getConfiguredDefaultHomeView(): 'listView' | 'calendarView' {
    const configuredView = (this.authService.getIOConfigurationValue(
      IoConfigurationFeatureCodes.BU_ACTIVITY_GA_HOME_DEFAULT_VIEW
    ) || '').toLowerCase();
    return configuredView === 'calendar' ? 'calendarView' : 'listView';
  }

  /**
   * Applies the configured default view on entry to the home and again after a
   * sync completes (IO configs load during initial sync, after the constructor
   * has already run). Skipped once the user has manually switched view this
   * session, so background delta syncs never pull them off their chosen view.
   */
  private applyConfiguredDefaultHomeView(): void {
    if (this.userSelectedHomeView) {
      return;
    }
    this.setHomeView(this.getConfiguredDefaultHomeView());
  }

  /** Sets the home landing view state (local + shared) without marking it a user choice. */
  private setHomeView(view: 'listView' | 'calendarView'): void {
    this.activityService.newHomeView = view;
    this.newHomeView = view;
    if (view === 'calendarView') {
      this.agendFooterService.actSegment = 'week';
      this.uiService.cellData = null;
      this.uiService.toolsActivityActive = true;
    } else {
      this.agendFooterService.actSegment = 'agenda';
      this.uiService.toolsActivityActive = false;
    }
  }

  ngOnInit() {
    this.events.observe('selected-nav-view').pipe(takeUntil(this.destroy$)).subscribe((view: string) => {
      if (view === 'dashboard' || view === 'calendar') {
        this.onTodayClick();
      }
    });

    // A completed sync (re)loads IO configs, so re-apply the configured default
    // home view. Reuses the existing post-sync 'refreshAgenda' signal published
    // by MasterDataService.syncData(); no-op once the user has manually switched
    // view this session.
    this.events.observe('refreshAgenda')
      .pipe(takeUntil(this.destroy$))
      .subscribe((arg) => {
        if (arg === 'sync successful') {
          this.applyConfiguredDefaultHomeView();
        }
      });
    this.syncCompleted$.pipe(
      takeUntil(this.destroy$),
      debounceTime(500)
    ).subscribe(() => {
      if (this.filtersApplied) {
        return;
      }
      const filters = this.searchConfigService.getDefaultFilters("Home");
      if (filters && filters.length > 0) {
        this.activityService.multiOptionActivityFilter = filters;
        this.events.publish('filterActivitiesDataForSelectedUser', 'MultipleOptionFilter');
      }
    });

    this.activityService.selectedActivity$.pipe(takeUntil(this.destroy$)).subscribe(activity => {
      this.hasSelectedActivity = activity != null;
    });
  }

  @HostListener('window:omni:sync:completed', ['$event'])
  @HostListener('window:omni:offlineDataLoad:Completed', ['$event'])
  syncCompleted() {
    this.syncCompleted$.next();
  }


  onInput(ev) {
    if (ev && ev.target) {
      this.activityService.activitiesSearchText = ev.target.value;
      this.activitiesPane?.refreshAgenda();
    }
  }

  ngAfterViewInit() {
    if (this.containerRef) {
      this.resizeObserver = new ResizeObserver(() => {
        this.onContainerResize();
      });
      this.resizeObserver.observe(this.containerRef.nativeElement);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeObserver && this.containerRef) {
      this.resizeObserver.unobserve(this.containerRef.nativeElement);
    }
  }
  

  get getTopActivityMonthYear(): string | null {
    let date = new Date();
    if (this.activityService.newHomeView === 'calendarView' && this.activityCalendar) {
      date = this.activityCalendar.masterSelectedDate;
    } else if (this.activityPaneDate) {
      date = new Date(this.activityPaneDate);
    }
    const formattedDate = this.dateTimeFormatsService.formatDate(date, "MMMM YYYY");
    return formattedDate;
  }

  public onActivityClick(event: any) {
    this.activityOpenedFromActivities.emit(event);
    this.calendarIsMaximized = false;
  }


  onTodayClick() {
    if (this.activityService.newHomeView === 'calendarView' && this.activityCalendar) {
      this.activityCalendar.scrollToToday();
    } else {
      this.activitiesPane?.scrollToDate();
    }
  }

  async openCalendar(event) {
    this.activityService.dateTimePickerType = DateTimeFieldType.StartDateField;
    this.calendarIsOpen = true;
    const dataRange = this.authService.getFromToDateRangeInUTCMiliSec();
    let selectedDate;
    let highlightRangeStart, highlightRangeEnd;
    if (this.activityService.newHomeView === 'calendarView' && this.activityCalendar) {
      selectedDate = this.activityCalendar.masterSelectedDate;
      // Use the actual visible range from the calendar
      if (typeof this.activityCalendar.getVisibleRange === 'function') {
        const range = this.activityCalendar.getVisibleRange();
        if (range && range.start && range.end) {
          highlightRangeStart = range.start;
          highlightRangeEnd = new Date(range.end);
          highlightRangeEnd.setDate(highlightRangeEnd.getDate() - 1);
        }
      }
      // fallback if not available
      if (!highlightRangeStart || !highlightRangeEnd) {
        const viewType = this.activityCalendar.currentViewType;
        if (viewType === 'Week') {
          const day = selectedDate.getDay();
          highlightRangeStart = new Date(selectedDate);
          highlightRangeStart.setDate(selectedDate.getDate() - day + 1); // Monday
          highlightRangeEnd = new Date(highlightRangeStart);
          highlightRangeEnd.setDate(highlightRangeStart.getDate() + 6); // Sunday
        } else if (viewType === '3 Days') {
          highlightRangeStart = new Date(selectedDate);
          highlightRangeEnd = new Date(selectedDate);
          highlightRangeEnd.setDate(highlightRangeStart.getDate() + 2);
        } else {
          highlightRangeStart = highlightRangeEnd = selectedDate;
        }
      }
    } else {
      if (this.activityPaneDate) {
        selectedDate = new Date(this.activityPaneDate);
      }
      selectedDate = selectedDate ?? new Date();
      highlightRangeStart = highlightRangeEnd = selectedDate;
    }
    const popover = await this.popoverCtrl.create(
      {
        component: GoAppCalendarComponent,
        componentProps: {
          currentViewPage: CurViewPageType.Appointment,
          fromDate: new Date(parseInt(dataRange.from)),
          toDate: new Date(parseInt(dataRange.to)),
          showMonthNavigation: true,
          selectedDate: selectedDate,
          highlightRangeStart,
          highlightRangeEnd
        },
        cssClass: "datetime-popover go-app-calendar",
        event
      });

    popover.onDidDismiss().then((data: any) => {
      this.calendarIsOpen = false;
      if (data && data.data && data.data.selectedDate) {
        const selectedDate = new Date(data.data.selectedDate);
        if (this.activityService.newHomeView === 'calendarView' && this.activityCalendar) {
          this.activityCalendar.masterSelectedDate = selectedDate;
          this.activityCalendar.selectedDate = selectedDate;
          this.activityCalendar.updateViewType(selectedDate);
        } else {
          this.activitiesPane?.scrollToDate(selectedDate);
        }
      }
    });

    popover.present();
  }


  openListView() {
    this.userSelectedHomeView = true;
    this.setHomeView('listView');
  }

  openCalendarView() {
    this.userSelectedHomeView = true;
    this.setHomeView('calendarView');
  }

  onContainerResize() {
    if (this.activityCalendar) {
      this.activityCalendar.updateViewType();
    }
  }

  onNewActivityOpened(event: boolean) {
    this.newActivityOpened.emit(event);
  }

  toggleSearchBar() {
    this.showSearchBar = !this.showSearchBar;
    if (!this.showSearchBar) {
      this.clearSearchBar();
    }
  }

  clearSearchBar() {
    this.activityService.activitiesSearchText = '';
    if (!this.uiService.toolsActivityActive) {
      this.activitiesPane?.refreshAgenda();
    }
  }

  async openFilterOptions() {

    const filterMenuModal = await this.modalCtrl.create({
      component: IndFilterMenuComponent,
      cssClass: 'filter-menu-right-modal-home-screen',
      componentProps: {
        viewData: {
          isMultipleSelectionEnabled: true,
          title: this.translate.instant('FILTERS'),
          isSearchEnabled: true,
          isReadOnly: false,
        },
        selectedFilter: this.activityService.multiOptionActivityFilter,
        from: PageName.GoAppActivitiesComponent,
        subFrom: ContactsModel.ACTIVITIES_TAB,
      },
      backdropDismiss: !this.device.isMobileDevicePortrait
    });
    filterMenuModal.present();
    filterMenuModal.onDidDismiss().then(async (obj: any) => {
      if (obj && obj.data && obj.data.isDone) {
        this.filtersApplied = true;
        // this.selectedFilters = obj.data.selectedItems;
        let type: ActivityType | 'MultipleOptionFilter' = ActivityType.AllActivity;
        if (obj.data.selectedItems.length == 0) {
          this.authService.impersonatedUser = undefined;
          this.activityService.agendaTeamViewActivities = [];
          this.activityService.multiOptionActivityFilter = [];
          type = ActivityType.AllActivity;
        } else if (obj.data.selectedItems.some(a => (a.categoryPath == "Users" && a.value != this.authService.user.systemUserID))) {
          this.authService.impersonatedUser = undefined;
          this.activityService.agendaTeamViewActivities = [];
          this.activityService.multiOptionActivityFilter = obj.data.selectedItems;
          type = 'MultipleOptionFilter';
        } else if (obj.data.selectedItems.some(a => a.categoryPath == "Activity_Type" && a.value == ActivityType.AllActivity)) {
          this.authService.impersonatedUser = undefined;
          this.activityService.agendaTeamViewActivities = [];
          this.activityService.multiOptionActivityFilter = [];
          type = ActivityType.AllActivity;
        } else {
          this.authService.impersonatedUser = undefined;
          this.activityService.agendaTeamViewActivities = [];
          this.activityService.multiOptionActivityFilter = obj.data.selectedItems;
          type = 'MultipleOptionFilter';
        }
        this.events.publish('filterActivitiesDataForSelectedUser', type);
      }
    });
  }
}
